import { type ComponentPropsWithRef, type Ref, useEffect, useRef } from 'react';

import { classNames } from '../../internal/classNames';

import { RiCheckboxBlankLine, RiCheckboxFill, RiCheckboxIndeterminateFill } from '@remixicon/react';

import Icon from '../Icon/Icon';

export type CheckboxProps = Omit<ComponentPropsWithRef<'input'>, 'type' | 'size' | 'onChange'> & {
    checked?: boolean;
    indeterminate?: boolean;
    onChange?: (checked: boolean) => void;
    ref?: Ref<HTMLInputElement>;
};

const Checkbox = ({
    checked = false,
    indeterminate = false,
    disabled = false,
    readOnly = false,
    className = '',
    onChange,
    onClick,
    ref,
    ...rest
}: CheckboxProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const icon = indeterminate
        ? RiCheckboxIndeterminateFill
        : checked
          ? RiCheckboxFill
          : RiCheckboxBlankLine;

    return (
        <span
            className={classNames({
                'ui-checkbox': true,
                'ui-checkbox--checked': checked && !indeterminate,
                'ui-checkbox--indeterminate': indeterminate,
                'ui-checkbox--disabled': disabled,
                [className]: Boolean(className),
            })}
            onClick={(e) => {
                if (e.target === inputRef.current || disabled || readOnly) return;
                // Cancel wrapping <label> activation; proxy click owns the toggle.
                e.preventDefault();
                e.stopPropagation();
                inputRef.current?.click();
            }}>
            <Icon
                icon={icon}
                size={16}
                className={classNames({
                    'ui-checkbox__icon': true,
                    'ui-checkbox__icon--checked': checked || indeterminate,
                    'ui-checkbox__icon--unchecked': !checked && !indeterminate,
                })}
            />
            <input
                {...rest}
                ref={setRefs}
                type='checkbox'
                checked={checked}
                disabled={disabled}
                readOnly
                onClick={onClick}
                onChange={(e) => {
                    e.currentTarget.indeterminate = indeterminate;
                    if (readOnly) return;
                    onChange?.(!checked);
                }}
            />
        </span>
    );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
